import React, { useCallback, MouseEventHandler, useMemo } from 'react';
import { Paper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useSelectedElement, useSelectedVersion } from '@client/store/ui';
import { useElementExpressionVariablesById } from '@client/hooks/use-element-expression-variables.hooks';
import {
  VARIABLE_NAMES_WITH_PERCENTAGES,
  getElementPropertyVariableNamesWithPercentages,
} from '@shared/helpers/expression-solving.helpers';
import { ExpressionVariables } from '@shared/helpers/expression-variables.helpers';
import { getVariableValueForExpressionSelectPreview } from '@shared/helpers/expression-select.helpers';
import { valueof } from '@shared/models/type-helpers.model';
import { getDisplayValueData } from '@shared/helpers/display/get-display-value-data.helper';

const MAX_SELECT_ITEMS = 14;
const UL_PADDING = 8;

interface ExpressionSelectProps {
  matchingExpressions: string[];
  activeExpressionIndex: number | undefined;
  onScroll: () => void;
  onClick: (expressionIndex?: number) => void;
  /** When provided, used for value preview (same variables as used for suggestions). */
  variables?: ExpressionVariables;
  /** Expression prefix ending with `).` when completing fn(args).property previews. */
  variablePreviewContext?: string;
}

const ExpressionSelect: React.FC<ExpressionSelectProps> = ({
  matchingExpressions,
  activeExpressionIndex,
  onScroll,
  onClick,
  variables: variablesProp,
  variablePreviewContext,
}) => {
  const { classes, cx } = useStyles();

  const selectedVersion = useSelectedVersion();
  const selectedElement = useSelectedElement();

  const expressionVariablesFromElement = useElementExpressionVariablesById(
    selectedElement?.id ?? selectedVersion?.id,
  );

  const expressionVariablesWithValues =
    variablesProp ?? expressionVariablesFromElement;

  const calculateAsPercentage = useMemo(
    () =>
      selectedElement
        ? {
            ...VARIABLE_NAMES_WITH_PERCENTAGES,
            ...getElementPropertyVariableNamesWithPercentages(
              selectedElement,
              selectedVersion,
            ),
          }
        : VARIABLE_NAMES_WITH_PERCENTAGES,
    [selectedVersion, selectedElement],
  );

  const handleClick: MouseEventHandler<HTMLLIElement> = useCallback(
    (event) => {
      event.stopPropagation();

      if (event.currentTarget.dataset.index === undefined) {
        return;
      }

      onClick(Number(event.currentTarget.dataset.index));
    },
    [onClick],
  );

  return (
    <Paper
      className={classes.expressionSelectContainer}
      onScroll={onScroll}
      style={{
        bottom: 118,
        height:
          matchingExpressions.length > 0
            ? UL_PADDING * 2 +
              Math.min(MAX_SELECT_ITEMS, matchingExpressions.length) * 36
            : 0,
      }}
      elevation={8}
    >
      <ul
        className={classes.expressionSelect}
        style={
          (activeExpressionIndex ?? 0) >= MAX_SELECT_ITEMS
            ? {
                transform: `translateY(-${
                  Math.min(
                    matchingExpressions.length - MAX_SELECT_ITEMS,
                    (activeExpressionIndex ?? 0) + 1 - MAX_SELECT_ITEMS,
                  ) * 36
                }px)`,
              }
            : {}
        }
      >
        {matchingExpressions.map((expression, index) => (
          <li
            key={expression}
            data-index={index}
            className={cx(
              classes.expressionSelectItem,
              activeExpressionIndex === index &&
                classes.expressionSelectItemActive,
            )}
            tabIndex={-1}
            onClick={handleClick}
          >
            {expression}
            <span className={classes.expressionValue}>
              {getExpressionValue(
                getVariableValueForExpressionSelectPreview(
                  expressionVariablesWithValues as Record<string, unknown>,
                  expression,
                  variablePreviewContext,
                ) as valueof<ExpressionVariables>,
                calculateAsPercentage[expression],
              )}
            </span>
          </li>
        ))}
      </ul>
    </Paper>
  );
};

const getExpressionValue = (
  expressionValue: valueof<ExpressionVariables>,
  calculateAsPercentage = false,
) => {
  const unit = calculateAsPercentage ? '%' : undefined;
  switch (typeof expressionValue) {
    case 'boolean':
      return expressionValue ? 'True' : 'False';
    case 'number':
      return getDisplayValueData({ value: expressionValue, unit }).full;
    default:
      return '';
  }
};

const useStyles = makeStyles()(({ spacing, palette }) => ({
  expressionSelectContainer: {
    position: 'absolute',
    animation: '$fadein 150ms ease-in-out',
    overflowY: 'auto',
    width: 635,
  },

  expressionSelect: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    margin: 0,
    padding: `${UL_PADDING}px 0`,
    listStyle: 'none',
  },

  expressionSelectItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `6px ${spacing(4)}`,
    color: '#515151',
    fontSize: '1rem',
    lineHeight: 1.5,
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    transition: 'background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
    '&:active': {
      backgroundColor: '#d5d9e8',
    },
  },

  expressionSelectItemActive: {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },

  expressionValue: {
    color: palette.text.secondary,
  },

  '@keyframes fadein': {
    from: {
      opacity: 0,
    },
    to: {
      opacity: 1,
    },
  },
}));

export default ExpressionSelect;
